import { useTranslation } from "@thyra/landing-i18n";
import {
  connectionInitial,
  connectionLogo,
  type CatalogConnection,
} from "../../lib/agent-catalog";

const LOGOS_SHOWN = 3;

/** Brand logo on a light tile (dark marks such as GitHub need it in dark mode too). Unknown providers get a neutral initial. */
function ProviderTile({ connection }: { connection: CatalogConnection }) {
  const src = connectionLogo(connection.key);
  return (
    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-logo-tile text-xs font-semibold text-logo-tile-foreground shadow-sm ring-2 ring-card">
      {src ? (
        <img
          src={src}
          alt=""
          width={16}
          height={16}
          className="h-4 w-4 object-contain"
          loading="lazy"
          decoding="async"
        />
      ) : (
        <span aria-hidden>{connectionInitial(connection.name)}</span>
      )}
    </span>
  );
}

function joinAsChoice(names: string[], locale: string): string {
  try {
    return new Intl.ListFormat(locale, {
      style: "long",
      type: "disjunction",
    }).format(names);
  } catch {
    return names.join(" / ");
  }
}

/** Accounts an agent works with: overlapping logos plus the real names from the API, and nothing when it needs none. */
export function ConnectionLogos({
  connections,
  optionalConnections = [],
}: {
  connections: CatalogConnection[];
  /** Shown, labelled optional, only when nothing is required; never replaces required accounts. */
  optionalConnections?: CatalogConnection[];
}) {
  const { t, i18n } = useTranslation("sections");
  const optionalOnly =
    connections.length === 0 && optionalConnections.length > 0;
  const list = optionalOnly ? optionalConnections : connections;

  if (list.length === 0) return null;

  const shown = list.slice(0, LOGOS_SHOWN);
  const extra = list.length - shown.length;
  const names = list.map((c) => c.name).join(", ");
  // Optional accounts are usually interchangeable (Gmail or Outlook), so read them as a choice.
  const apps = optionalOnly
    ? joinAsChoice(
        list.map((c) => c.name),
        i18n.language,
      )
    : names;

  return (
    <div className="flex h-6 items-center gap-2">
      <div className="flex shrink-0 -space-x-2" aria-hidden>
        {shown.map((connection) => (
          <ProviderTile key={connection.key} connection={connection} />
        ))}
        {extra > 0 ? (
          <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-muted px-1.5 text-[11px] font-semibold text-muted-foreground-strong ring-2 ring-card">
            +{extra}
          </span>
        ) : null}
      </div>
      <p
        className="min-w-0 truncate text-xs font-medium text-muted-foreground-strong"
        title={names}
      >
        {optionalOnly
          ? t("assistantRoles.optionalWorksWith", { apps })
          : t("assistantRoles.worksWith", { apps })}
      </p>
    </div>
  );
}
